import invariant from "tiny-invariant"
import { contourSize } from "./contour"
import { flagEnabled } from "./flags"
import ClientData from "@/common/client_data"
import env from "@/common/env"
import { sizeUnit } from "@/common/locales"
import { keys, objEmpty } from "@/common/util"
import Calc from "@/common/util/calculator"
import Sign from "@/signomatic/app/sign"
import { arrayUniq } from "@/signomatic/util"
import ColorUtil from "@/signomatic/util/colors"
import Fonts, { type Fontdata } from "@/signomatic/util/fonts"

const woodColors = [
  ["#f6e6b5", "#a88f71"],
  ["#be9261", "#8e7a64"],
  ["#e8cca1", "#a67e6d"],
  ["#eed895", "#a88f71"],
]
const woodBackgrounds = woodColors.map(item => item[0])
const woodContentColors = woodColors.map(item => item[1])

type OptionType = "shape" | "fixmethod" | "border" | "material" | "pattern"

export const allPresets = (data: SignData): SignPreset[] => {
  let presets: SignPreset[] = []

  for (const key in data.preset_groups) {
    if (Object.prototype.hasOwnProperty.call(data.preset_groups, key)) {
      const elements = data.preset_groups[key]?.presets || []
      presets = presets.concat(...elements)
    }
  }

  return presets
}

export const matchPreset = (state: SignState, preset: SignPreset, exact?: boolean, ignoreName = false): boolean => {
  const settings = preset.settings

  if (preset.group_alias && exact) {
    return false
  }

  if (state.preset && preset.name && !ignoreName) {
    return state.preset === preset.name
  }

  if (Object.prototype.hasOwnProperty.call(preset, "badge_name")) {
    if (!preset.badge_name && state.badgeName) {
      return false
    } else if (state.badgeName !== preset.badge_name) {
      return false
    }
  }

  if (preset.material && state.material !== preset.material) {
    return false
  }

  if (settings.locale && !settings.locale.includes(env.locale)) {
    return false
  }

  if (!settings.multicolor && settings.color && state.color !== settings.color) {
    return false
  }

  if (
    settings.dualcolor &&
    settings.fixed_colors &&
    ColorUtil.isColorSets(settings.fixed_colors) &&
    !settings.fixed_colors.find(set => set[0] === state.color && set[1] === state.contentColor)
  ) {
    return false
  }

  if (
    !settings.dualcolor &&
    settings.fixed_colors &&
    !ColorUtil.isColorSets(settings.fixed_colors) &&
    !settings.fixed_colors.find(set => set === state.color)
  ) {
    return false
  }

  if (settings.fixmethods) {
    if ((settings.fixmethods.length > 1 && state.fixmethod) || exact) {
      if (!settings.fixmethods.some(method => method.name === state.fixmethod)) {
        return false
      }
    }
  }

  if (settings.shapes) {
    if (!settings.shapes.some(shape => shape.name === state.shape)) {
      return false
    }
  }

  if (settings.borders) {
    if (!settings.borders.some(border => border.name === state.border)) {
      return false
    }
  }

  if (settings.fixed_size) {
    if (settings.fixed_size.w !== state.width) {
      return false
    }

    if (settings.fixed_size.h !== state.height) {
      return false
    }
  }

  return true
}

export const dataWithPreset = (data: SignData, state?: SignState): SignData => {
  const presets = allPresets(data)
  if (!presets || presets.length === 0) {
    return data
  }

  const match = state ? presets.find(preset => matchPreset(state, preset)) || presets[0] : presets[0]
  invariant(match)
  const settings = match.settings
  const newData = { ...data }

  if (match.material) {
    newData.materials = [{ name: match.material }]
  } else if (match.settings.materials) {
    newData.materials = match.settings.materials
  }

  if (settings.min_size) {
    newData.min_size = settings.min_size
  }

  if (settings.max_size) {
    newData.max_size = settings.max_size
  } else if (settings.fixed_size) {
    newData.max_size = settings.fixed_size
  }

  if (Object.prototype.hasOwnProperty.call(settings, "autosize")) {
    newData.autosize = !!settings.autosize
  }

  if (Object.prototype.hasOwnProperty.call(settings, "dualcolor")) {
    newData.dualcolor = !!settings.dualcolor
    newData.multicolor = !settings.dualcolor
  }

  if (Object.prototype.hasOwnProperty.call(settings, "multicolor")) {
    newData.multicolor = !!settings.multicolor
  }

  if (settings.borders) {
    newData.borders = settings.borders
  }

  if (settings.fixmethods) {
    newData.fixmethods = settings.fixmethods
  }

  if (settings.shapes) {
    newData.shapes = settings.shapes
  }

  if (settings.fixed_colors) {
    newData.fixed_colors = settings.fixed_colors
  }

  if (settings.suggested_colors) {
    newData.suggested_colors = settings.suggested_colors
  }

  if (settings.default_color) {
    newData.default_color = settings.default_color
  }

  if (settings.suggested_sizes) {
    newData.suggested_sizes = settings.suggested_sizes
  }

  if (settings.suggested_sizes_in) {
    newData.suggested_sizes_in = settings.suggested_sizes_in
  }

  if (settings.production) {
    newData.production = settings.production
  }

  if (settings.admin_only) {
    newData.admin_only = settings.admin_only
  }

  if (settings.suggested_thickness) {
    newData.suggested_thickness = settings.suggested_thickness
  }

  if (settings.admin_colors) {
    newData.admin_colors = settings.admin_colors
  }

  return newData
}

export const dataWithFiltersOverride = (data: SignData, state?: SignState): SignData => {
  const newData = { ...data }

  const shapeData = data.shapes.filter(shape => shape.name == state?.shape)[0]

  if (shapeData) {
    if (shapeData.filters?.suggested_sizes) {
      newData.suggested_sizes = shapeData.filters.suggested_sizes
    }

    if (shapeData.filters?.suggested_sizes_in) {
      newData.suggested_sizes_in = shapeData.filters.suggested_sizes_in
    }
  }

  return newData
}

export const dataWithSignTypeOverride = (
  data: SignData,
  type: string | null,
  state?: SignState,
  fontdata?: Fontdata
): SignData => {
  const metaProduct = state?.metaProduct
  const metaType = data.name

  if ((type || metaType) && fontdata) {
    data.fonts = Fonts.enabledFonts(type || metaType, fontdata, data.font_set || "")
  }

  if (!metaProduct) {
    return data
  }

  const metaData = ClientData.signData?.find(data => data.name === metaProduct) as SignData
  const overrides = metaData.signtype_overrides || {}
  const override = overrides[metaType]

  const newData = { ...data }

  // override metaType
  if (metaData.min_size) {
    if (metaData.min_size.w > newData.min_size.w) {
      newData.min_size.w = metaData.min_size.w
    }
    if (metaData.min_size.h > newData.min_size.h) {
      newData.min_size.h = metaData.min_size.h
    }
  }

  if (metaData.max_size) {
    if (metaData.max_size.w < newData.max_size.w) {
      newData.max_size.w = metaData.max_size.w
    }
    if (metaData.max_size.h < newData.max_size.h) {
      newData.max_size.h = metaData.max_size.h
    }
  }

  if (metaData.signtypes) {
    newData.signtypes = metaData.signtypes
  }

  // override signtype
  if (!override) {
    return newData
  }

  if (override.suggested_sizes) {
    newData.suggested_sizes = override.suggested_sizes
  }

  if (override.suggested_sizes_in) {
    newData.suggested_sizes_in = override.suggested_sizes_in
  }

  if (override.fixmethods) {
    const overrideFixmethods = [...override.fixmethods]
    overrideFixmethods.forEach((fixmethod, i) => {
      const initFixmethod = newData.fixmethods.filter(initFixmethod => initFixmethod.name == fixmethod.name)[0]
      overrideFixmethods[i] = { ...initFixmethod, ...fixmethod }
    })
    newData.fixmethods = overrideFixmethods
  }

  if (override.shapes) {
    const overrideShapes = [...override.shapes]
    overrideShapes.forEach((shape, i) => {
      const initShape = newData.shapes.filter(initShape => initShape.name == shape.name)[0]
      overrideShapes[i] = { ...initShape, ...shape }
    })
    newData.shapes = overrideShapes
  }

  if (override.materials && newData.materials) {
    const overrideMaterials = [...override.materials]
    overrideMaterials.forEach((material, i) => {
      const initMaterial = newData.materials.filter(initMaterial => initMaterial.name == material.name)[0]
      invariant(initMaterial)
      const filters = { ...initMaterial.filters, ...material.filters }
      overrideMaterials[i] = { ...initMaterial, filters }
    })
    newData.materials = overrideMaterials
  }

  if (override.preset_groups && newData.preset_groups) {
    // we want to preserve initial preset_groups, but override, if present in new data
    const initKeys = Object.keys(newData.preset_groups)
    initKeys.forEach(key => {
      const newPreset = (override.preset_groups || {})[key]
      if (newPreset && newData.preset_groups) {
        newData.preset_groups[key] = newPreset
      }
    })
  }

  return newData
}

/**
 * Matches a sign option against sign data and state and returns if it's allowed and the reason
 * if it's not allowed
 */
export const validateOption = (
  option: SignDataOption,
  state: SignState,
  type: OptionType,
  validateFixedSize = false,
  signCLI = false
): [boolean, string[]] => {
  const { filters } = option
  const reasons: string[] = []

  if (!filters) {
    return [true, reasons]
  }

  if (filters.admin && !(ClientData.admin || signCLI)) {
    return [false, ["admin"]]
  }

  if (filters.border_color_blacklist && filters.border_color_blacklist.length > 0) {
    if (
      state.border &&
      state.border !== "none" &&
      state.borderColor &&
      filters.border_color_blacklist.includes(state.borderColor)
    ) {
      reasons.push("border_color")
    }
  }

  if (filters.content_color_blacklist && filters.content_color_blacklist.length > 0) {
    const borderColors = state.objects.map(obj => obj.border?.borderColor)
    const borderFillColors = state.objects.map(obj => obj.border?.color)
    const contentColors = state.objects.map(obj => obj.color)

    if (contentColors.some(color => filters.content_color_blacklist?.includes(color))) {
      reasons.push("content_color")
    }

    if (borderColors.some(color => filters.content_color_blacklist?.includes(color || ""))) {
      reasons.push("content_color")
    }

    if (borderFillColors.some(color => filters.content_color_blacklist?.includes(color || ""))) {
      reasons.push("content_color")
    }
  }

  if (filters.color_blacklist && filters.color_blacklist.length > 0) {
    if (filters.color_blacklist.includes(state.color)) {
      reasons.push("color")
    }
  }

  if (filters.color_whitelist && filters.color_whitelist.length > 0) {
    if (!filters.color_whitelist.includes(state.color)) {
      reasons.push("color")
    }
  }

  if (filters.fixmethod_blacklist && filters.fixmethod_blacklist.length > 0) {
    if (state.fixmethod && filters.fixmethod_blacklist.includes(state.fixmethod)) {
      reasons.push("fixmethod")
    }
  }

  if (filters.fixmethod_whitelist && filters.fixmethod_whitelist.length > 0) {
    if (state.fixmethod && !filters.fixmethod_whitelist.includes(state.fixmethod)) {
      reasons.push("fixmethod")
    }
  }

  if (filters.material_whitelist && filters.material_whitelist.length > 0) {
    if (state.material && !filters.material_whitelist.includes(state.material)) {
      reasons.push("material")
    }
  }

  if (filters.legacy) {
    for (const key of keys(state)) {
      if (key === type && state[key] !== option.name) {
        reasons.push("legacy")
      }
    }
  }

  if (filters.locale && filters.locale.length > 0) {
    if (!filters.locale.includes(env.locale)) {
      reasons.push("locale")
    }
  }

  if (validateFixedSize && filters.fixed_size) {
    if (filters.fixed_size.w > 0 && filters.fixed_size.w !== state.width) {
      reasons.push("fixed_size")
    }

    if (filters.fixed_size.h > 0 && filters.fixed_size.h !== state.height) {
      reasons.push("fixed_size")
    }
  }

  if (filters.min_size && filters.min_size.length > 0) {
    for (const size of filters.min_size) {
      if (!size.filters || size.filters.shape === state.shape) {
        if (size.w > 0 && state.width < size.w) {
          reasons.push("size")
        }

        if (size.h > 0 && state.height < size.h) {
          reasons.push("size")
        }
      }
    }
  }

  if (filters.max_size && filters.max_size.length > 0) {
    for (const size of filters.max_size) {
      if (!size.filters || size.filters.shape === state.shape) {
        if (size.w > 0 && state.width > size.w) {
          reasons.push("size")
        }
        if (size.h > 0 && state.height > size.h) {
          reasons.push("size")
        }
      }
    }
  }

  if (filters.min_flanged_width) {
    if (["direction_left", "direction_right"].includes(state.shape)) {
      const aw = state.height / 2
      const flatWidth = state.width - aw

      if (flatWidth < filters.min_flanged_width) {
        reasons.push("size")
      }
    }
  }

  if (filters.shape_blacklist && filters.shape_blacklist.length > 0) {
    if (state.shape && filters.shape_blacklist.includes(state.shape)) {
      reasons.push("shape")
    }
  }

  if (filters.shape_whitelist && filters.shape_whitelist.length > 0) {
    if (state.shape && !filters.shape_whitelist.includes(state.shape)) {
      reasons.push("shape")
    }
  }

  if (filters.thickness_blacklist && filters.thickness_blacklist.length > 0) {
    if (state.thickness && filters.thickness_blacklist.includes(state.thickness.toString())) {
      reasons.push("thickness")
    }
  }

  return [reasons.length === 0, arrayUniq(reasons)]
}

export const applyPreset = (state: SignState, preset: SignPreset): Partial<SignState> => {
  const { shapes, fixed_size, fixmethods, multicolor, color, dualcolor, fixed_colors, borders, admin_colors } =
    preset.settings
  const { badge_name, material } = preset

  const newState: Partial<SignState> = {
    badgeName: badge_name,
    preset: preset.name,
  }

  if (material) {
    newState.material = material
  } else if (preset.settings.materials) {
    const enabled = preset.settings.materials.filter(m => validateOption(m, state, "material"))
    invariant(enabled[0])
    newState.material = enabled[0].name
  }

  if (fixed_size) {
    newState.width = fixed_size.w
    newState.height = fixed_size.h
    newState.widthAuto = false
    newState.heightAuto = false
  }

  const enabledFixmethods = (fixmethods || []).map(fixmethod => fixmethod.name)

  if (fixmethods) {
    if (
      state.fixmethod == "tape" &&
      !enabledFixmethods.includes("tape") &&
      enabledFixmethods.includes("self_adhesive_pad")
    ) {
      state.fixmethod = "self_adhesive_pad"
    }

    newState.fixmethod =
      !fixmethods.some(method => method.name === state.fixmethod) && fixmethods[0]
        ? fixmethods[0].name
        : state.fixmethod

    const stainlessLegacyBadgeNames = [
      "stainless_28x49",
      "stainless_30x30",
      "stainless_60x18",
      "stainless_60x30",
      "stainless_50x50",
      "stainless_80x25",
      "stainless_100x40",
      "stainless_150x50",
      "stainless_170x70",
    ]

    const stainlessLegacy = state.badgeName && stainlessLegacyBadgeNames.includes(state.badgeName)

    const validFixmethod = fixmethods.some(method => method.name === state.fixmethod)
    const sameBadgeName = newState.badgeName === state.badgeName
    const keepFixmethod = validFixmethod && (sameBadgeName || stainlessLegacy)

    if (fixmethods.length > 1 && !keepFixmethod) {
      newState.fixmethod = undefined
    }
  }

  if (shapes) {
    newState.shape = !shapes.some(shape => shape.name === state.shape) && shapes[0] ? shapes[0].name : state.shape
  }

  if (borders) {
    newState.border =
      !borders.some(border => border.name === state.border) && borders[0] ? borders[0].name : state.border
  }

  if (color && (!multicolor || state.material !== material)) {
    newState.color = color
  }

  if (color === null && state.material !== material) {
    newState.color = "#ffffff"
  }

  if (material === "aluminium_lacquered" && ["stainless", "brass"].includes(state.material)) {
    newState.color = "#eeeeee"
  }

  if (
    dualcolor &&
    fixed_colors &&
    ColorUtil.isColorSets(fixed_colors) &&
    !(
      fixed_colors.find(set => set[0] === state.color && set[1] === state.contentColor) ||
      (admin_colors && admin_colors.find(set => set[0] === state.color && set[1] === state.contentColor))
    )
  ) {
    newState.color = fixed_colors[0][0]
    newState.contentColor = fixed_colors[0][1]
  }

  if (
    !dualcolor &&
    fixed_colors &&
    !ColorUtil.isColorSets(fixed_colors) &&
    !fixed_colors.find(set => set === state.color)
  ) {
    newState.color = fixed_colors[0]
  }

  return newState
}

const forceFixedSize = (state: SignState, option?: SignDataOption) => {
  if (option && option.filters) {
    if (option.filters.fixed_size) {
      const { w, h } = option.filters.fixed_size

      if (w > 0) {
        state.width = w
        state.widthAuto = false
      }

      if (h > 0) {
        state.height = h
        state.heightAuto = false
      }
    }

    if (option.filters.shape_whitelist) {
      if (!option.filters.shape_whitelist.includes(state.shape)) {
        state.shape = option.filters.shape_whitelist[0]
      }
    }

    if (option.filters.fixmethod_blacklist) {
      if (state.fixmethod && option.filters.fixmethod_blacklist.includes(state.fixmethod)) {
        state.fixmethod = undefined
      }
    }
  }
}

export const currentMinText = (data: SignData, state?: SignState, font?: string): number => {
  const base = data.min_text.find(r => objEmpty(r.filters))
  let min = base?.size || 1

  for (const rule of data.min_text) {
    if (state && rule.filters?.shape_whitelist && rule.filters?.shape_whitelist.includes(state.shape)) {
      min = Math.max(rule.size, min)
    }
    if (font && rule.filters?.font_whitelist && rule.filters?.font_whitelist.includes(font)) {
      min = Math.max(rule.size, min)
    }
  }

  return min
}

export const correctFontSize = (
  size: number,
  data: SignData,
  state: SignState,
  fontName?: string,
  text?: string
): number => {
  // Size is being calculated dynamically "getCableTagSize", no need to update it
  if (state.metaType === "thermal_transfer_label") {
    return size
  }

  const min = currentMinText(data, state, fontName)

  size = Math.max(min, size)
  size = Math.min(data.max_size.h, size)

  if (state.shape === "contour" && state.contour && fontName && text) {
    const font = Fonts.getOpentypeFont(fontName, "normal")

    if (font) {
      const max = Calc.mmToPx(data.max_size.w)
      const longest = text.split(/\r?\n/).sort((a, b) => b.length - a.length)[0]
      const current = font.getAdvanceWidth(longest, Calc.fontsizeToPx(size))

      if (current > max) {
        size *= max / (current + contourSize(state.contour) * 11)
      }
    }
  } else if (fontName === "Braille (SE 6p)") {
    size = 10
  }

  return size
}

/**
 * Validates and corrects given state base on sign data.
 * ! Order of validations matters
 */
export const correctState = (
  state: SignState,
  type: string,
  data: SignData,
  switchedType?: string | null,
  update?: Partial<SignState>,
  signCLI?: boolean,
  fontdata?: Fontdata
) => {
  state.objects ??= []

  if (state.metaProduct && !state.metaType) {
    state.metaType = type
  }

  const switched = !!switchedType
  let fullData = dataWithPreset(data, state)
  const presets = allPresets(data)

  fullData = dataWithSignTypeOverride(fullData, type, state, fontdata)

  state.switched = switched

  const oindices = state.objects.map(obj => obj.oindex)
  const uniqueOIndices = oindices.filter((v, i, a) => a.indexOf(v) === i)
  const sortedObjects = state.objects.sort((a, b) => (a.oindex || 0) - (b.oindex || 0))

  // we have duplicate oindices
  if (oindices.length !== uniqueOIndices.length) {
    for (let index = 0; index < sortedObjects.length; index++) {
      const element = sortedObjects[index]
      element.oindex = index
    }
  }

  if (update?.width) {
    state.width = Math.round(state.width)
  }

  if (update?.height) {
    state.height = Math.round(state.height)
  }

  if (update?.border !== "none" && state.border === "none" && fullData.default_color) {
    state.borderColor = fullData.default_color
  }

  if (switched && state.fixedPrice) {
    if (ClientData.admin && ClientData.editing && ClientData.editing.admin && ClientData.editing.typeUpdate) {
      state.fixedPrice = false
    } else {
      delete state.fixedPrice
    }
  }

  if (switched && ["aluminium_sign", "forex_sign", "solid_plastic_sign", "magnetic_sign"].includes(type)) {
    state.shape = "rect"
  }

  if (!presets || presets.length === 0) {
    state.badgeName = undefined
    state.preset = undefined
  }

  if (switchedType == "etched_aluminium_sign" && type == "label_plates") {
    state.preset = "label_anodised_aluminium"
  }

  if (presets && presets.length > 0) {
    const ignoreStatePreset = state.metaProduct === "meta_name_sign" && !!update?.metaType
    let match = presets.find(preset => matchPreset(state, preset, true, ignoreStatePreset))

    // in case of deprecated named preset try to find the first preset that's close enough
    if (!match && state.preset && !presets.map(p => p.name).includes(state.preset)) {
      match = presets.find(preset => matchPreset(state, preset, true, true))

      if (match && match.group_alias) {
        const group = (data.preset_groups || {})[match.name]

        if (group) {
          match = (group.presets || [])[0]
        }
      }
    }

    if (!match && type === "label_plates" && state.material === "stainless" && state.badgeName !== undefined) {
      match = presets.find(preset => preset.name === "label_laser")
    }

    if (!match) {
      const withMilitary = presets.find(
        p => p.settings.fixmethods && p.settings.fixmethods.some(f => f.name === "military") && p.material === "plastic"
      )
      match = state.fixmethod === "military" && withMilitary ? withMilitary : presets[0]
    }

    state = { ...state, ...applyPreset(state, match) }
    fullData = dataWithSignTypeOverride(dataWithPreset(data, state), type, state, fontdata)
  }

  if (fullData.double_sided) {
    state.doubleSided = true

    if (switched) {
      state.side = 0
    }
  } else {
    state.doubleSided = false
    state.side = 0
  }

  if (state.doubleSided) {
    if (state.side === undefined || state.side === null) {
      state.side = 0
    }

    if (!state.sideState) {
      const sideState: SignSideState = {
        border: state.border,
        color: state.color,
        borderColor: state.borderColor,
      }
      state.sideState = [{ ...sideState }, { ...sideState }]
    } else {
      if (update?.border) {
        state.sideState[state.side].border = update.border
      }

      if (update?.color) {
        state.sideState[state.side].color = update.color
      }

      if (update?.borderColor) {
        state.sideState[state.side].borderColor = update.borderColor
      }

      const sideState = state.sideState[state.side]
      invariant(sideState)

      if (sideState.border) {
        state.border = sideState.border
      }

      if (sideState.color) {
        state.color = sideState.color
      }

      if (sideState.borderColor) {
        state.borderColor = sideState.borderColor
      }
    }
  }

  // autosize
  if (state.shape === "contour" || type === "contour_cut") {
    state.widthAuto = state.heightAuto = true

    if (state.shape === "contour" && !state.contour) {
      state.contour = "s"
    }
  } else if (!fullData.autosize) {
    state.widthAuto = state.heightAuto = false
  }

  if (switched && type === "vinyl_text") {
    state.widthAuto = state.heightAuto = true
  }

  // colors
  state.dualColor = fullData.dualcolor
  state.multiColor = fullData.multicolor

  for (const obj of state.objects) {
    obj.multicolor = state.multiColor
  }

  if (fullData.materials.some(item => item.name === "wood") && woodBackgrounds.includes(state.color)) {
    state.material = "wood"
  }

  if (state.color === "none" && !fullData.transparent) {
    state.color = "#ffffff"
  }

  if (fullData.name === "acryl_alu_sign") {
    for (const obj of state.objects) {
      if (obj.border) {
        if (["#daa520", "#bbbbbb"].includes(obj.border.borderColor)) {
          obj.border.borderColor = "#000040"
        }
        if (obj.border.color) {
          if (["#daa520", "#bbbbbb"].includes(obj.border.color)) {
            obj.border.color = "#000000"
          }
        }
      }
      if (obj.color) {
        if (["#daa520", "#bbbbbb"].includes(obj.color)) {
          obj.color = "#ffffff"
        }
      }
    }
    if (state.borderColor === "#daa520" || state.borderColor === "#bbbbbb") {
      state.borderColor = "#000000"
    }
  }

  if (fullData.transparent) {
    state.color = "none"

    const sets = fullData.fixed_colors as string[]
    const contentColors = arrayUniq(state.objects.map(obj => obj.color))

    if (
      !sets.includes(state.contentColor || "") ||
      !sets.includes(state.borderColor || "") ||
      contentColors.length > 1
    ) {
      state.contentColor = sets.includes("#000000") ? "#000000" : "#ffffff"
    }

    state.borderColor = state.contentColor
  } else if (fullData.dualcolor) {
    const adminColors = ClientData.admin || signCLI ? fullData.admin_colors : []
    const sets = [...fullData.fixed_colors, ...adminColors] as Array<[string, string]>
    const match = sets.find(set => set[0] === state.color && set[1] === state.contentColor)

    if (!match) {
      const backgroundMatch = sets.find(set => set[0] === state.color)
      state.color = backgroundMatch ? backgroundMatch[0] : sets[0][0]
      state.contentColor = backgroundMatch ? backgroundMatch[1] : sets[0][1]
    }

    state.borderColor = state.contentColor
  } else if (fullData.multicolor) {
    if (type === "double_sided_sign") {
      if (
        ["#c27268"].includes(state.color) ||
        (state.material === "plastic" && ["#bbbbbb", "#daa520", "#eeeeee"].includes(state.color))
      ) {
        state.color = (fullData.suggested_colors[0] as string) || "#ffffff"
      }
    }

    if (switched) {
      if (type === "acryl_sign") {
        state.color = fullData.suggested_colors[0] as string
      }

      if (type === "acryl_alu_sign") {
        state.color = "#eeeeee"
      }

      if (
        [
          "aluminium_sign",
          "sticker",
          "banner",
          "rollup",
          "forex_sign",
          "solid_plastic_sign",
          "magnetic_sign",
          "double_sided_sign",
          "poster",
          "ecoboard",
        ].includes(type)
      ) {
        if (
          [
            "#f0f0f0",
            "#bbbbbb",
            "#daa520",
            "#c27268",
            "#eeeeee",
            "#ffe472",
            "#e3e0db",
            "#d8c98d",
            "#c8c6cd",
            "#252526",
            "#eed895",
            "#be9261",
            "#e8cca1",
            "#f6e6b5",
            "#e3e0dc",
          ].includes(state.color)
        ) {
          const colors =
            fullData.fixed_colors && fullData.fixed_colors.length > 0
              ? fullData.fixed_colors
              : fullData.suggested_colors

          state.color = (colors[0] as string) || "#ffffff"
          if (type === "double_sided_sign") {
            const sideState = state.sideState || []
            sideState[0].color = (colors[0] as string) || "#ffffff"
            sideState[1].color = (colors[0] as string) || "#ffffff"
          }
        }
      }
    } else {
      if (fullData.banned_colors?.includes(state.color)) {
        const colors =
          fullData.fixed_colors && fullData.fixed_colors.length > 0 ? fullData.fixed_colors : fullData.suggested_colors
        state.color = (colors[0] as string) || "#ffffff"
        if (type === "double_sided_sign") {
          const sideState = state.sideState || []
          sideState[0].color = (colors[0] as string) || "#ffffff"
          sideState[1].color = (colors[0] as string) || "#ffffff"
        }
      }
    }
  }

  if (!state.color) {
    state.color = "#ffffff"
  }

  // fixmethods that force sign size
  if (update && update.fixmethod) {
    const option = fullData.fixmethods.find(method => method.name === update.fixmethod)
    forceFixedSize(state, option)
  }

  // shapes that force sign size
  if (update && update.shape) {
    const option = fullData.shapes.find(method => method.name === update.shape)
    forceFixedSize(state, option)
  }

  if (switched && type === "rollup") {
    state.fixmethod = "stand"
  }

  // objects
  if (state.objects.length > 0) {
    if (!fullData.object_borders) {
      for (const obj of state.objects) {
        obj.border = undefined
      }
    }

    if (!fullData.images) {
      state.objects = state.objects.filter(o => !Sign.isImage(o))
    }

    if (!fullData.texts) {
      state.objects = state.objects.filter(o => !Sign.isText(o))
    }

    if (fullData.single_object && state.objects.length > 0) {
      state.objects = [state.objects[0]]
    }

    let contourPadding = 0

    if (state.shape === "contour" && state.contour) {
      contourPadding = contourSize(state.contour) / 0.1

      for (const obj of state.objects) {
        obj.border = undefined
      }
    }

    for (const obj of state.objects) {
      if (Sign.isImage(obj)) {
        if (update && state.shape === "contour" && type === "sticker") {
          obj.width = Math.max(
            Calc.mmToPx(fullData.min_size.w),
            Math.min(obj.width, Calc.mmToPx(fullData.max_size.w) - contourPadding)
          )
          obj.height = Math.max(
            Calc.mmToPx(fullData.min_size.h),
            Math.min(obj.height, Calc.mmToPx(fullData.max_size.h) - contourPadding)
          )
        } else {
          obj.width = Math.max(2, Math.min(obj.width, Calc.mmToPx(fullData.max_size.w) - contourPadding))
          obj.height = Math.max(2, Math.min(obj.height, Calc.mmToPx(fullData.max_size.h) - contourPadding))
        }
      }
    }

    if ((fullData.transparent || fullData.dualcolor) && state.contentColor) {
      for (const obj of state.objects) {
        obj.color = state.contentColor

        if (obj.border) {
          obj.border.borderColor = state.contentColor
          obj.border.color = undefined
        }
      }
    } else if (fullData.multicolor) {
      if (switched) {
        for (const obj of state.objects) {
          if (Sign.isText(obj)) {
            if (woodContentColors.includes(obj.color)) {
              obj.color = "#000000"
            }

            if (obj.border && woodContentColors.includes(obj.border.borderColor)) {
              obj.border.borderColor = "#000000"
            }

            obj.color = ColorUtil.filterCustomColor(obj.color, type, state)
            if (obj.border) {
              obj.border.borderColor = ColorUtil.filterCustomColor(obj.border.borderColor, type, state)
            }
          }
        }
      }

      if (!state.contentColor && state.objects.length > 0) {
        state.contentColor = state.objects[0].color
      }
    }

    if (type === "sticker" && state.shape === "contour" && state.objects.length === 1) {
      for (const obj of state.objects) {
        obj.angle = 0
      }
    }

    const zindices = state.objects.map(obj => obj.zindex)
    const uniqueIndices = zindices.filter((v, i, a) => a.indexOf(v) === i)

    // we have duplicate zindices
    if (zindices.length !== uniqueIndices.length) {
      const objects = state.objects.sort((a, b) => a.zindex - b.zindex)

      for (let index = 0; index < objects.length; index++) {
        const element = objects[index]
        element.zindex = index
      }
    }

    const fontSet = fullData.fonts?.map(f => f.toLowerCase()) || []
    const disabledFonts = Fonts.disabledFonts(type, state.material).map(font => font.toLowerCase())

    // fonts
    for (const obj of state.objects) {
      if (Sign.isText(obj)) {
        if (
          !obj.font ||
          (disabledFonts.includes(obj.font.toLowerCase()) && !ClientData.editing) ||
          !fontSet.includes(obj.font.toLowerCase())
        ) {
          obj.font = "Arial"
        }

        if (type === "tactile_sign") {
          if (obj.font === "WorkSans") {
            obj.text = obj.text.toUpperCase()
          }

          if (!["WorkSans", "Braille (SE 6p)"].includes(obj.font)) {
            obj.font = "Braille (SE 6p)"
          }
        }

        const allowedStyles = Fonts.allowedStyles(obj.font, fontdata)

        if (!allowedStyles.boldItalic && obj.bold && obj.italic) {
          obj.bold = false
          obj.italic = false
        }

        if (!allowedStyles.bold) {
          obj.bold = false
        }

        if (!allowedStyles.italic) {
          obj.italic = false
        }

        if (obj.underlined && Fonts.disableUnderline(obj.font, type)) {
          obj.underlined = false
        }

        if (obj.mirrored && !(fullData.transparent || fullData.name == "sticker")) {
          obj.mirrored = false
        }

        obj.size = correctFontSize(obj.size, fullData, state, obj.font, obj.text)
      }
    }
  }

  // sides
  for (const obj of state.objects) {
    if (obj.side === undefined || obj.side === null) {
      obj.side = 0
    }
  }

  // size
  const originalWidth = state.width

  if (sizeUnit === "in") {
    if (fullData.fixed_sizes_in && fullData.fixed_sizes_in.length > 0) {
      const match = fullData.fixed_sizes_in.some(size => {
        const [w, h] = size.split("x").map(s => parseFloat(s))

        return Math.round(w * 10) / 10 === state.widthIn && Math.round(h * 10) / 10 === state.heightIn
      })

      if (!match) {
        const [w, h] = fullData.fixed_sizes_in[0].split("x").map(s => parseFloat(s))
        invariant(w)
        invariant(h)
        state.width = Calc.inToMm(w)
        state.height = Calc.inToMm(h)
      }
    }
  } else {
    if (fullData.fixed_sizes && fullData.fixed_sizes.length > 0) {
      const match = fullData.fixed_sizes.some(size => {
        const [w, h] = size.split("x").map(s => parseFloat(s))

        return w === state.width && h === state.height
      })

      if (!match) {
        const [w, h] = fullData.fixed_sizes[0].split("x").map(s => parseFloat(s))
        state.width = w
        state.height = h
      }
    }
  }

  if (switched && type === "poster") {
    state.width = sizeUnit === "in" ? Calc.inToMm(8.3) : 210
    state.height = sizeUnit === "in" ? Calc.inToMm(11.7) : 297
  }

  const fixmethodData = fullData.fixmethods.find(m => m.name === state.fixmethod)

  if (fixmethodData && fixmethodData.filters && fixmethodData.filters.fixed_size) {
    const fixedSize = fixmethodData.filters.fixed_size

    if (!fixedSize.filters || fixedSize.filters.shape === state.shape) {
      if (fixedSize.w > 0 && state.width > fixedSize.w) {
        state.width = fixedSize.w
      }

      if (fixedSize.h > 0 && state.height > fixedSize.h) {
        state.height = fixedSize.h
      }
    }

    if (fixmethodData.filters.max_size && fixmethodData.filters.max_size.length > 0) {
      for (const size of fixmethodData.filters.max_size) {
        if (!size.filters || size.filters.shape === state.shape) {
          if (size.w > 0 && state.width > size.w) {
            state.width = size.w
          }

          if (size.h > 0 && state.height > size.h) {
            state.height = size.h
          }
        }
      }
    }
  }

  const newMaxSize = { w: 150, h: 100 }
  const requirementsForNewMaxSize =
    state.thickness === 0.8 && !state.thicknessLegacySizes && type === "plastic_sign" && !ClientData.admin
  const maxSize = requirementsForNewMaxSize ? newMaxSize : fullData.max_size
  state.width = Math.max(fullData.min_size.w, state.width)
  state.width = Math.min(state.width, maxSize.w)

  state.height = Math.max(fullData.min_size.h, state.height)
  state.height = Math.min(state.height, maxSize.h)

  const rescaled = state.width / originalWidth

  if (switched && rescaled < 1) {
    for (const obj of state.objects) {
      if (Sign.isImage(obj)) {
        obj.width = obj.width * rescaled
        obj.height = obj.height * rescaled
      }

      if (Sign.isText(obj)) {
        obj.size = obj.size * rescaled
      }
    }
  }

  // update the shape only after updated the size
  if (type === "thermal_transfer_label") {
    if (state.width === 60 || state.width === 75) {
      state.shape = "round"
    } else {
      state.shape = "stop"
    }
  }

  // filtered options
  const enabledShapes = fullData.shapes
    .filter(shape => validateOption(shape, state, "shape", true)[0])
    .map(shape => shape.name)

  if (!enabledShapes.includes(state.shape)) {
    state.shape = enabledShapes[0]
  }

  const enabledFixmethods = fullData.fixmethods
    .filter(fixmethod => validateOption(fixmethod, state, "fixmethod", true, signCLI)[0])
    .map(fixmethod => fixmethod.name)

  const visibleFixmethods = ClientData.admin
    ? fullData.fixmethods
    : fullData.fixmethods.filter(fixmethod => !fixmethod.filters?.admin)

  const defaultFixmethod = visibleFixmethods.length === 1 || type === "rollup"

  if (
    state.fixmethod == "tape" &&
    !enabledFixmethods.includes("tape") &&
    enabledFixmethods.includes("self_adhesive_pad")
  ) {
    state.fixmethod = "self_adhesive_pad"
  }

  if (!state.fixmethod || !enabledFixmethods.includes(state.fixmethod)) {
    state.fixmethod = defaultFixmethod ? enabledFixmethods[0] : undefined
  }

  const enabledBorders = fullData.borders
    .filter(border => validateOption(border, state, "border", true)[0])
    .map(border => border.name)

  if (!enabledBorders.includes(state.border)) {
    state.border = enabledBorders[0] === "none" && enabledBorders.length > 1 ? enabledBorders[1] : enabledBorders[0]
  }

  if (fullData.materials.length > 0) {
    const enabledMaterials = fullData.materials
      .filter(material => validateOption(material, state, "material", true)[0])
      .map(material => material.name)

    if (!enabledMaterials.includes(state.material)) {
      state.material = enabledMaterials[0]
    }
  } else {
    state.material = "regular"
  }

  if (update?.color) {
    state.pattern = undefined
  }

  if (fullData.patterns.length > 0) {
    const enabledPatterns = fullData.patterns
      .filter(pattern => validateOption(pattern, state, "pattern", true)[0])
      .map(pattern => pattern.name)

    if (state.pattern && !enabledPatterns.includes(state.pattern)) {
      state.pattern = enabledPatterns[0]
    }
    if (state.pattern) {
      state.color = "#ffffff"
    }
  } else {
    delete state.pattern
  }

  // this needs to be after material and color validations
  if (state.doubleSided) {
    const sideState = state.sideState || []

    if (["aluminium", "aluminium_lacquered"].includes(state.material || "")) {
      state.color = "#eeeeee"
      sideState[0].color = "#eeeeee"
      sideState[1].color = "#eeeeee"
    } else {
      if (sideState[0].color === "#eeeeee") {
        sideState[0].color = "#ffffff"
      }

      if (sideState[1].color === "#eeeeee") {
        sideState[1].color = "#ffffff"
      }
    }
  }

  // thickness
  const labelAnodisedAluminium = type === "label_plates" && state.material === "aluminium"
  const labelLacqueredAluminium = type === "label_plates" && state.material === "aluminium_lacquered"
  const aluLabelAndPlates = labelAnodisedAluminium || labelLacqueredAluminium

  if (state.thickness && !aluLabelAndPlates) {
    if (type !== "plastic_sign" || !["", "none", "tape"].includes(state.fixmethod || "")) {
      state.thickness = undefined
    }
  }

  if (aluLabelAndPlates && fullData.suggested_thickness && !state.thickness) {
    if (labelAnodisedAluminium) {
      state.thickness = parseFloat(fullData.suggested_thickness[1])
    } else if (labelLacqueredAluminium) {
      state.thickness = parseFloat(fullData.suggested_thickness[0])
    }
  }

  if (switched) {
    if (state.thickness) {
      state.thickness = undefined
    }
    if (type === "plastic_sign" && fullData.suggested_thickness) {
      state.thickness = parseFloat(fullData.suggested_thickness[0])
    }
  }

  if (update && type === "plastic_sign" && fullData.suggested_thickness) {
    if (
      (state.color === "#ffffff" && state.contentColor === "#000000") ||
      (state.color === "#141414" && state.contentColor === "#ffffff")
    ) {
      if (state.thickness === 0.8 && !state.fixmethod) {
        state.fixmethod = "none"
      }
    } else {
      if (state.thickness === 0.8) {
        state.color = "#ffffff"
        state.contentColor = "#000000"
        state.borderColor = "#000000"
        state.objects.forEach(obj => (obj.color = "#000000"))
      }
    }
  }

  if (type === "thermal_transfer_label" && state.width === 30 && ["#007cb0", "#9b2423"].includes(state.color)) {
    state.color = "#ffffff"
  }

  // custom holes
  const customHolesFlag = flagEnabled("custom-holes-9377")
  const customHolesConditions =
    state.fixmethod &&
    ["screw", "tape_and_screw", "custom_holes"].includes(state.fixmethod) &&
    state.shape === "rect" &&
    (type === "label_plates" || state.material === "stainless") &&
    state.material !== "brass"

  if (
    !customHolesFlag &&
    !customHolesConditions &&
    (state.customHoleCount || state.customHoleDistanceMm || state.customHoleSizeMm || state.customHoleControlPattern)
  ) {
    state.customHoleCount = undefined
    state.customHoleDistanceMm = undefined
    state.customHoleSizeMm = undefined
    state.customHoleControlPattern = undefined
  }

  return state
}

/**
 * Returns default state for the sign type
 */
export const defaultState = (
  type: string,
  metaProduct: string | undefined,
  data: SignData,
  switchedType?: string | null
) => {
  const fullData = dataWithPreset(data)
  const defaultFixmethod = fullData.fixmethods.length === 1 || type === "rollup"

  let state: SignState = {
    version: 3,
    width: fullData.min_size.w,
    widthPx: Calc.mmToPx(fullData.min_size.w),
    widthIn: Calc.mmToIn(fullData.min_size.w),
    height: fullData.min_size.h,
    heightPx: Calc.mmToPx(fullData.min_size.h),
    heightIn: Calc.mmToIn(fullData.min_size.h),
    shape: fullData.shapes[0].name,
    fixmethod: defaultFixmethod ? fullData.fixmethods[0].name : undefined,
    dualColor: fullData.dualcolor,
    multiColor: fullData.multicolor,
    widthAuto: false,
    heightAuto: false,
    border: "none",
    color: "#ffffff",
    contentColor: "#000000",
    borderColor: "#000000",
    material: "regular",
    badgeName: undefined,
    objects: [],
    switched: !!switchedType,
    doubleSided: false,
    seenBside: false,
    thickness: undefined,
    metaProduct,
  }

  state.width = defaultSize(type).width
  state.height = defaultSize(type).height

  if (fullData.autosize || state.shape === "contour" || type === "contour_cut") {
    state.widthAuto = true
    state.heightAuto = true
  }

  if (fullData.fixed_colors && fullData.fixed_colors.length > 0) {
    const color = fullData.fixed_colors[0]
    state.color = color[0]
    state.contentColor = color[1]
    state.borderColor = color[1]
  } else if (fullData.suggested_colors && fullData.suggested_colors.length > 0) {
    state.color = fullData.suggested_colors[0]
  }

  const presets = allPresets(data)

  if (presets && presets.length > 0) {
    state = { ...state, ...applyPreset(state, presets[0]) }
  }

  if (!defaultFixmethod) {
    state.fixmethod = undefined
  }

  if (fullData.suggested_thickness) {
    state.thickness = parseFloat(fullData.suggested_thickness[0])
  }

  return correctState(state, type, data, switchedType)
}

const defaultSize = (type: string) => {
  let width = 0
  let height = 0

  switch (type) {
    case "sticker":
    case "magnetic_sign": {
      width = sizeUnit === "in" ? Calc.inToMm(6) : 150
      height = sizeUnit === "in" ? Calc.inToMm(4) : 100
      break
    }
    case "banner": {
      width = sizeUnit === "in" ? Calc.inToMm(79) : 2000
      height = sizeUnit === "in" ? Calc.inToMm(39) : 1000
      break
    }
    case "aluminium_sign":
    case "ecoboard":
    case "solid_plastic_sign":
    case "forex_sign": {
      width = sizeUnit === "in" ? Calc.inToMm(5) : 120
      height = sizeUnit === "in" ? Calc.inToMm(2) : 50
      break
    }
    case "color_badge": {
      width = sizeUnit === "in" ? Calc.inToMm(2.362) : 60
      height = sizeUnit === "in" ? Calc.inToMm(1.181) : 30
      break
    }
    case "brass_sign": {
      width = sizeUnit === "in" ? Calc.inToMm(4.094) : 104
      height = sizeUnit === "in" ? Calc.inToMm(2.362) : 60
      break
    }
    case "plastic_sign": {
      width = sizeUnit === "in" ? Calc.inToMm(2.8) : 70
      height = sizeUnit === "in" ? Calc.inToMm(1.2) : 30
      break
    }
    case "double_sided_sign": {
      width = sizeUnit === "in" ? Calc.inToMm(5) : 120
      height = sizeUnit === "in" ? Calc.inToMm(2.8) : 70
      break
    }
    case "poster": {
      width = sizeUnit === "in" ? Calc.inToMm(8.3) : 210
      height = sizeUnit === "in" ? Calc.inToMm(11.7) : 297
      break
    }
    case "tactile_sign": {
      width = sizeUnit === "in" ? Calc.inToMm(6) : 150
      height = sizeUnit === "in" ? Calc.inToMm(6) : 150
      break
    }
    case "etched_aluminium_sign": {
      width = sizeUnit === "in" ? Calc.inToMm(2.4) : 60
      height = sizeUnit === "in" ? Calc.inToMm(1.2) : 30
      break
    }
    case "stainless_steel_sign": {
      width = sizeUnit === "in" ? Calc.inToMm(2.4) : 60
      height = sizeUnit === "in" ? Calc.inToMm(0.7) : 18
      break
    }
    case "label_plates": {
      width = sizeUnit === "in" ? Calc.inToMm(2.4) : 60
      height = sizeUnit === "in" ? Calc.inToMm(0.7) : 18
      break
    }
    case "thermal_transfer_label": {
      width = sizeUnit === "in" ? Calc.inToMm(2.4) : 60
      height = sizeUnit === "in" ? Calc.inToMm(0.5) : 12
      break
    }
  }

  return { width: width, height: height }
}

export const optionForcedState = (option: SignDataOption, state: SignState, forced: string[]) => {
  const { filters } = option
  const forcedState: Partial<SignState> = {}

  if (forced.includes("size")) {
    if (filters?.min_size && filters.min_size.length > 0) {
      for (const size of filters.min_size) {
        if (!size.filters || size.filters.shape === state.shape) {
          if (size.w > 0 && state.width < size.w) {
            forcedState.width = size.w
          }

          if (size.h > 0 && state.height < size.h) {
            forcedState.height = size.h
          }
        }
      }
    }

    if (filters?.max_size && filters.max_size.length > 0) {
      for (const size of filters.max_size) {
        if (!size.filters || size.filters.shape === state.shape) {
          if (size.w > 0 && state.width > size.w) {
            forcedState.width = size.w
          }

          if (size.h > 0 && state.height > size.h) {
            forcedState.height = size.h
          }
        }
      }
    }
  }

  if (forced.includes("shape")) {
    if (filters?.shape_whitelist && filters.shape_whitelist.length > 0) {
      if (state.shape && !filters.shape_whitelist.includes(state.shape)) {
        forcedState.shape = filters.shape_whitelist[0]
      }
    }
  }

  return forcedState
}

export const reduceOption = (options: SignDataOption[], state: SignState, type: OptionType) => {
  return options.map(option => {
    const [enabled, reason] = validateOption(option, state, type)
    return { ...option, enabled, disabled_reason: reason }
  })
}

export const presetNames = (data: SignData): string[] => {
  const names: string[] = []

  for (const key in data.preset_groups) {
    if (Object.prototype.hasOwnProperty.call(data.preset_groups, key)) {
      names.push(key)
    }
  }

  return names
}

export const currentPresetSettings = (state: SignState, data: SignData): SignPresetSettings | null => {
  const presets = allPresets(data)
  const current = presets.find(preset => matchPreset(state, preset))
  return current ? current.settings : null
}
